/**
 * Keeps a business invite attached to the user across the sign-in detour.
 *
 * An invitee lands on `/join/:inviteId` while logged out. Getting them back to
 * that exact URL after authentication is the whole job, and there are two very
 * different gaps to bridge:
 *
 *  - The in-app hop (`/join/:id` -> `/login` -> `/join/:id`) only needs the id
 *    kept in the URL, which `loginPathForLocation` handles.
 *  - The hosted-UI hop leaves the origin entirely. `signInWithRedirect` is a
 *    full page navigation, so component state, props and the query string are
 *    all gone by the time the user comes back. Amplify replays `customState`
 *    to us as a `customOAuthState` Hub event, but that event can fire before a
 *    listener is attached. sessionStorage is the durable half of the pair: it
 *    is scoped to this tab, survives the round trip to Cognito, and does not
 *    depend on listener timing.
 *
 * The pending id is consume-once. Reading it clears it, so an abandoned invite
 * cannot keep yanking the user back to the join page on every later page load.
 */

const PENDING_INVITE_KEY = 'dupe.pendingInviteId';

// Safari private mode and hardened browser settings can throw on access.
function safeSessionStorage() {
    try {
        return window.sessionStorage;
    } catch (error) {
        return null;
    }
}

export function setPendingInviteId(inviteId) {
    if (!inviteId) return;
    try {
        safeSessionStorage()?.setItem(PENDING_INVITE_KEY, inviteId);
    } catch (error) {
        // Storage unavailable: the customOAuthState Hub event is the fallback.
    }
}

export function peekPendingInviteId() {
    try {
        return safeSessionStorage()?.getItem(PENDING_INVITE_KEY) || null;
    } catch (error) {
        return null;
    }
}

export function clearPendingInviteId() {
    try {
        safeSessionStorage()?.removeItem(PENDING_INVITE_KEY);
    } catch (error) {
        // Nothing to clean up if storage is unavailable.
    }
}

/** Reads the pending invite id and clears it in the same step. */
export function consumePendingInviteId() {
    const inviteId = peekPendingInviteId();
    if (inviteId) clearPendingInviteId();
    return inviteId;
}

export function inviteIdFromPath(pathname) {
    const match = /^\/join\/([^/?#]+)/.exec(pathname || '');
    return match ? decodeURIComponent(match[1]) : null;
}

export function joinPath(inviteId) {
    return '/join/' + encodeURIComponent(inviteId);
}

/**
 * Where to send someone who needs to authenticate, without losing the invite
 * they arrived with. Both `/join/:id` and an already-decorated
 * `/login?inviteId=:id` have to round-trip, because signing out mid-login
 * re-enters this path.
 */
export function loginPathForLocation(location) {
    const fromPath = inviteIdFromPath(location?.pathname);
    const fromQuery = new URLSearchParams(location?.search || '').get('inviteId');
    const inviteId = fromPath || fromQuery || peekPendingInviteId();
    return inviteId ? '/login?inviteId=' + encodeURIComponent(inviteId) : '/login';
}

/**
 * Where to land after a successful sign-in. Prefers the id the caller already
 * has; otherwise consumes one stashed before a hosted-UI redirect.
 */
export function resolvePostLoginPath(inviteId) {
    const pending = consumePendingInviteId();
    const resolved = inviteId || pending;
    return resolved ? joinPath(resolved) : '/';
}
